import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Stack, IconButton, Divider, MenuItem, Snackbar, Alert
} from '@mui/material';
import { Add, Delete, PointOfSale, Receipt } from '@mui/icons-material';
import { posAPI } from '../services/api';
import { CURRENCY_SYMBOL, formatCurrency } from '../utils/currency';
import PageHeader from '../components/PageHeader';
export default function POS() {
  const [items, setItems] = useState([{ name: '', quantity: 1, price: 0 }]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  const addItem = () => setItems([...items, { name: '', quantity: 1, price: 0 }]);
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const subtotal = items.reduce((acc, item) => acc + (item.quantity * item.price), 0);
  const tax = subtotal * 0.05; // 5% flat tax example
  const total = subtotal + tax;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      await posAPI.createTransaction({
        items,
        subtotal,
        tax,
        total,
        paymentMethod
      });
      setSnackbar({ open: true, message: `Transaction Complete! Total: ${formatCurrency(total.toFixed(2))} paid via ${paymentMethod.toUpperCase()}`, severity: 'success' });
      setItems([{ name: '', quantity: 1, price: 0 }]);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Transaction failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Point of Sale (POS)"
        subtitle="Ring up walk-in purchases and merchandise"
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'background.paper' }}>
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    <TableCell width="120">Qty</TableCell>
                    <TableCell width="150">Price</TableCell>
                    <TableCell width="150">Total</TableCell>
                    <TableCell width="50"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField 
                          size="small" 
                          fullWidth 
                          placeholder="e.g. Water Bottle" 
                          value={item.name} 
                          onChange={(e) => updateItem(index, 'name', e.target.value)} 
                        />
                      </TableCell>
                      <TableCell>
                        <TextField 
                          size="small" 
                          type="number" 
                          fullWidth 
                          inputProps={{ min: 1 }} 
                          value={item.quantity} 
                          onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))} 
                        />
                      </TableCell>
                      <TableCell>
                        <TextField 
                          size="small" 
                          type="number" 
                          fullWidth 
                          value={item.price} 
                          onChange={(e) => updateItem(index, 'price', Number(e.target.value))} 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{formatCurrency((item.quantity * item.price).toFixed(2))}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton color="error" onClick={() => removeItem(index)}><Delete /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <Button startIcon={<Add />} onClick={addItem}>Add Item</Button>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={2}>Summary</Typography>
            <Stack spacing={1.5} mb={3}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography fontWeight={600}>{formatCurrency(subtotal.toFixed(2))}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Tax (5%)</Typography>
                <Typography fontWeight={600}>{formatCurrency(tax.toFixed(2))}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6" fontWeight={800}>Total</Typography>
                <Typography variant="h6" fontWeight={800} color="primary">{formatCurrency(total.toFixed(2))}</Typography>
              </Stack>
            </Stack>

            <TextField 
              select 
              fullWidth 
              label="Payment Method" 
              value={paymentMethod} 
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ mb: 3 }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Credit/Debit Card</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
            </TextField>

            <Button 
              variant="contained" 
              fullWidth 
              size="large" 
              startIcon={<Receipt />}
              onClick={handleCheckout}
              disabled={items.length === 0 || total === 0 || loading}
            >
              {loading ? 'Processing...' : 'Complete Sale'}
            </Button>
          </Paper>
        </Box>
      </Stack>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
